import React, { useEffect, useRef, useState } from 'react';
import { startAppleLogin } from '../api/auth';

export const APPLE_VERIFIER_KEY = 'apple.loginVerifier';
const toHex = (bytes: Uint8Array) => Array.from(bytes, value => value.toString(16).padStart(2, '0')).join('');

export default function AppleSignInButton({ isLoading, onError, onBusy }: {
  isLoading: boolean;
  onError: (message: string | null) => void;
  onBusy: (busy: boolean) => void;
}) {
  const [starting, setStarting] = useState(false);
  const pending = useRef(false);

  useEffect(() => {
    const resume = (event: PageTransitionEvent) => {
      if (!event.persisted) return;
      pending.current = false;
      setStarting(false);
      onBusy(false);
    };
    window.addEventListener('pageshow', resume);
    return () => window.removeEventListener('pageshow', resume);
  }, [onBusy]);

  const signIn = async () => {
    if (pending.current || isLoading) return;
    pending.current = true;
    setStarting(true);
    onBusy(true);
    onError(null);
    try {
      const verifier = toHex(crypto.getRandomValues(new Uint8Array(32)));
      const binding = toHex(new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier))));
      sessionStorage.setItem(APPLE_VERIFIER_KEY, verifier);
      window.location.assign(await startAppleLogin(binding));
    } catch {
      sessionStorage.removeItem(APPLE_VERIFIER_KEY);
      pending.current = false;
      setStarting(false);
      onBusy(false);
      onError('Apple sign-in could not be completed. Please try again.');
    }
  };

  return (
    <button type="button" onClick={signIn} disabled={isLoading || starting}
      className="mb-3 flex h-11 w-full items-center justify-center rounded-lg bg-white disabled:opacity-50"
      aria-label="Sign in with Apple">
      <img src="/apple-sign-in.png" alt="Sign in with Apple" className="h-11 max-w-full object-contain" />
    </button>
  );
}
